import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { ListGroup, ListGroupItem, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  getCleverSections as getCleverSectionsFromBackend,
  getAllMyCleverStudents,
  getJob,
  syncClassroom as importClassroom,
} from '../BackendInterface';
import { ImportSource } from '../CleverRostering/CleverRosteringTypes';
import Spinner from 'react-spinkit';
import { getStore } from '../ApplicationState';
import { logger } from '../error-tracker';
import { isGoogleApiInitialized, signOutGoogleAccount } from '../UserManagementHelpers';
import styles from '../SCSS/ImportClassroomModal.module.scss';
import { useErrorStatus } from '../components/EnableErrorMessagePopup/EnableErrorMessagePopup';
import {
  ClassroomType,
  CleverApiSection,
  CleverApiStudent,
  JobError,
  MergeOrSyncClassroomRequest,
  UserProfileType,
} from '../../peekapak-types/DataProtocolTypes';

const IconWithTooltip = ({ tipText, children }: { tipText: string; children: JSX.Element }) => (
  <OverlayTrigger overlay={<Tooltip id={tipText}>{tipText}</Tooltip>} delayShow={300} delayHide={150}>
    {children}
  </OverlayTrigger>
);

type GoogleUserProfile = {
  googleAccountUserId: string;
};
type CleverUserProfile = {
  cleverAccountUserId: string;
  cleverAccessToken: {
    token: string;
    expiry: number;
  };
  cleverProfile: {
    sections: Array<string>;
  };
};
type Props = {
  googleAccountUserId: string;
  show: boolean;
  title: string;
  importSource: string | undefined;
  onHide: () => void;
  onDone: (arg0: null | ClassroomType) => void;
  userProfile: GoogleUserProfile | CleverUserProfile | UserProfileType;
  cleverImportInvoked: boolean;
};
type StudentContainer = {
  profile: {
    id: string;
    name: {
      givenName: string;
      familyName: string;
    };
    emailAddress?: string;
    grade?: string;
  };
};
type ExternalClassroom = {
  id: string;
  name: string;
  section?: string;
  grade?: string;
  students: Array<StudentContainer>;
};
type ExternalClassroomsList = {
  source: ImportSource.GOOGLE | ImportSource.CLEVER | '';
  list: Array<ExternalClassroom>;
};

const ImportClassroomModal = (props: Props) => {
  const [isDoneFetching, setIsDoneFetching] = useState<string | undefined>(undefined);
  const [errorState, setErrorState] = useState<string | Error | undefined>(undefined);
  const [classrooms, setClassrooms] = useState<ExternalClassroomsList>({
    source: '',
    list: [],
  });
  const [isImporting, setIsImporting] = useState(false);
  const [importPercentage, setImportPercentage] = useState(0);
  const prevExternalUserId = useRef<string>();
  const { cleverImportInvoked, importSource, userProfile, show, onHide } = props;
  const onShowErrorMessage = useErrorStatus();
  useEffect(() => {
    // console.debug(`%cuseEffect called`, 'background: blue; color: yellow');
    async function fetchGoogleClassrooms() {
      if (isNothingToDo()) {
        // console.debug( `%cFetching google classrooms... nothing to do`, 'color: red; background: black');
        return;
      }

      if (!('googleAccountUserId' in userProfile) || !userProfile.googleAccountUserId) {
        logger.logException(
          new Error(`ImportClassroomModal: fetchGoogleClassrooms called on non-Google connected account`),
        );
        return;
      }

      // reset error state otherwise render will
      // render that instead
      setErrorState('');
      prevExternalUserId.current = userProfile.googleAccountUserId;
      setIsDoneFetching('FETCHING');

      try {
        const tempClassrooms = await (async () => {
          let rawCourses;
          try {
            rawCourses = await gapi.client.classroom.courses.list();
            const tempClassrooms: Array<ExternalClassroom> | undefined = rawCourses.result.courses;

            if (!tempClassrooms) {
              signOutGoogleAccount();
              throw new Error('No Google courses');
            }

            return tempClassrooms;
          } catch (error) {
            logger.logMessage(`Exception caught enumerating Google Classroom courses list`);
            logger.logMessage('Raw courses = ' + JSON.stringify(rawCourses));
            throw error;
          }
        })();

        const studentsPromises = tempClassrooms.map((classroom) => fetchStudents(classroom.id));

        // console.debug( `%cFetching students...`, 'background: black; color: red');
        const rawStudents = await Promise.all(studentsPromises);
        tempClassrooms.forEach((classroom, i) => {
          classroom.students = rawStudents[i];
        });
        setClassrooms({
          source: ImportSource.GOOGLE,
          list: tempClassrooms,
        });
        setIsDoneFetching('DONE');
      } catch (error) {
        setErrorState(error as Error);
        logger.logException(error as Error);
      }

      async function fetchStudents(courseId: string) {
        const firstFetch = await gapi.client.classroom.courses.students.list({
          courseId,
        });

        if (!firstFetch.result.students) {
          return [];
        }

        if (!firstFetch.result.nextPageToken) {
          return firstFetch.result.students;
        }

        let list: gapi.client.classroom.Student[] | undefined;
        let nextPageToken: string | undefined | null;
        let firstResult: gapi.client.classroom.ListStudentsResponse;
        try {
          firstResult = firstFetch.result;
          list = [...(firstResult.students as gapi.client.classroom.Student[])];
          nextPageToken = firstResult.nextPageToken;
        } catch (error) {
          logger.logMessage('Exception caught while fetching first page of students');
          logger.logMessage('Student objects result = ' + JSON.stringify(firstFetch.result));
          throw error;
        }

        let fetchIteration;
        try {
          // must store reference to result otherwise after
          // first access, it will get uninitialized
          while (nextPageToken) {
            fetchIteration = await gapi.client.classroom.courses.students.list({
              courseId,
              pageToken: nextPageToken,
            });

            const currentStudentsList = fetchIteration?.result?.students;
            if (currentStudentsList) {
              list = [...list, ...currentStudentsList];
              nextPageToken = fetchIteration.result.nextPageToken;
            } else {
              nextPageToken = null;
            }
          }
        } catch (error) {
          logger.logMessage('Exception caught while fetching next pages of students');
          logger.logMessage('fetchIteration = ' + JSON.stringify(fetchIteration?.result));
          throw error;
        }

        return list;
      }

      function isNothingToDo() {
        // console.debug( `%cisNothingToDo called`, 'background: blue; color: yellow');
        const noAssociatedGoogleUser = () =>
          !('googleAccountUserId' in userProfile) || !userProfile.googleAccountUserId;

        const inStateOfError = () => !!errorState;

        const dataReadySameUser = () =>
          !errorState &&
          isDoneFetching === 'DONE' &&
          prevExternalUserId.current === (userProfile as GoogleUserProfile | UserProfileType).googleAccountUserId &&
          classrooms.source === ImportSource.GOOGLE;

        if (noAssociatedGoogleUser()) return true;
        if (!isGoogleApiInitialized()) return true;
        if (inStateOfError()) return true;
        if (dataReadySameUser()) return true;
        if (isDoneFetching === 'FETCHING') return true;
        return false;
      }
    }

    async function fetchCleverClassrooms() {
      if (isNothingToDo()) {
        // console.debug( `%cFetching clever classrooms... nothing to do`, 'color: red; background: black');
        return;
      }
      if (!('cleverAccountUserId' in userProfile) || !userProfile.cleverAccountUserId) {
        logger.logException(
          new Error(`ImportClassroomModal: fetchCleverClassrooms called on non-Clever connected account`),
        );
        return;
      }

      // reset error state otherwise render will
      // render that instead
      setErrorState('');
      prevExternalUserId.current = userProfile.cleverAccountUserId;
      setIsDoneFetching('FETCHING');

      try {
        const sections = await getCleverSections();
        const mappedClassrooms: ExternalClassroom[] = getExternalClassroomsFromCleverSections(sections);
        const sectionsWithStudents = await getSectionsWithStudents(mappedClassrooms);
        setClassrooms({
          source: ImportSource.CLEVER,
          list: sectionsWithStudents,
        });
        setIsDoneFetching('DONE');
      } catch (error) {
        setErrorState(error as Error);
        logger.logException(error as Error);
      }

      function getExternalClassroomsFromCleverSections(sections: CleverApiSection[]) {
        return sections.map((section) => {
          return {
            id: section.data.id,
            name: section.data.name,
            grade: section.data.grade || '',
            students: section.data.students,
          };
        });
      }

      async function getSectionsWithStudents(sections: ExternalClassroom[]) {
        const authAccessCode = (userProfile as CleverUserProfile | UserProfileType).cleverAccessToken?.token || '';
        const myUserId = (userProfile as CleverUserProfile | UserProfileType).cleverAccountUserId || '';
        const newClassrooms = [];

        const allMyStudents = await getAllMyCleverStudents(authAccessCode, myUserId);

        const allMyStudentsMap = (() => {
          const map: Record<string, CleverApiStudent> = {};
          let i = 0;
          while (i < allMyStudents.data.length) {
            map[allMyStudents.data[i].data.id] = allMyStudents.data[i];
            i += 1;
          }
          return map;
        })();

        let i = 0;
        while (i < sections.length) {
          const curSection = sections[i];
          const cleverStudents = (() => {
            const s = [];
            let j = 0;
            while (j < (curSection.students?.length || 0)) {
              if (Object.hasOwn(allMyStudentsMap, curSection.students?.[j])) {
                s.push(allMyStudentsMap[curSection.students?.[j]]);
              }
              j += 1;
            }
            return s;
          })();
          const externalStudents = cleverStudents.map((student) => {
            return {
              profile: {
                id: student.data.id,
                name: {
                  givenName: student.data.name.first,
                  familyName: student.data.name.last || '',
                },
                grade: student.data.grade,
              },
            };
          });
          newClassrooms.push({ ...curSection, students: externalStudents });
          i += 1;
        }

        return newClassrooms;
      }

      async function getCleverSections() {
        const authAccessCode = (userProfile as CleverUserProfile | UserProfileType).cleverAccessToken?.token || '';
        const cleverUserId = (userProfile as CleverUserProfile | UserProfileType).cleverAccountUserId || '';
        const sections = await getCleverSectionsFromBackend(authAccessCode, cleverUserId);

        return sections.data;
      }

      function isNothingToDo() {
        const noAssociatedCleverUser = () =>
          !('cleverAccountUserId' in userProfile) || !userProfile.cleverAccountUserId;

        const isUserHasntChanged = () =>
          prevExternalUserId.current === (userProfile as CleverUserProfile | UserProfileType).cleverAccountUserId;

        const inStateOfError = () => !!errorState;

        const dataReadySameUser = () => !errorState && isDoneFetching === 'DONE' && isUserHasntChanged();

        const sourceHasntChanged = () => classrooms.source === ImportSource.CLEVER;

        const noErrorAndStillFetching = () => !errorState && isDoneFetching !== undefined;

        if (noAssociatedCleverUser()) return true;
        if (inStateOfError()) return true;
        if (dataReadySameUser()) return true;
        if (noErrorAndStillFetching() && sourceHasntChanged()) return true;
        return false;
      }
    }

    function fetchClassrooms() {
      if (importSource === ImportSource.GOOGLE) {
        // console.debug( `%cImporting from Google`, `color: blue; background: yellow` );
        fetchGoogleClassrooms();
      } else if (importSource === ImportSource.CLEVER) {
        // console.debug( `%cImporting from Clever`, `color: blue; background: yellow` );
        fetchCleverClassrooms();
      }
    }

    fetchClassrooms();
  }, [cleverImportInvoked, errorState, isDoneFetching, userProfile, importSource, classrooms.source, show]);

  /* eslint-enable complexity */
  useEffect(() => {
    function checkAndSetError() {
      if (show && errorState) {
        const [errorDisplay, errorTitle] = getErrorDisplays();
        onShowErrorMessage(errorDisplay, errorTitle, () => {
          setErrorState('');
          onHide();
        });
      }
    }

    checkAndSetError();

    /* eslint-disable complexity */
    function getErrorDisplays() {
      let em, ed, et;

      if (typeof errorState === 'string') {
        em = errorState;
      } else if (errorState instanceof Error) {
        em = errorState.message;
      } else {
        em = JSON.stringify(errorState);
      }

      switch (em) {
        case 'Google API not initialized':
          ed = 'You must log in using your Google account to use this feature';
          et = 'Google account not connected';
          break;

        case 'No Google courses':
          ed = `This Google account doesn't have any courses/classrooms to import`;
          et = 'No courses found';
          break;

        case 'No Clever courses':
          ed = `This Clever account doesn't have any courses/classrooms to import`;
          et = 'No courses found';
          break;

        default:
          ed = `We cannot proceed. Please send a copy of this error message to Peekapak Support: ${em}`;
          et = 'Cannot import classroom';
      }

      return [ed, et];
    }
  }, [show, onHide, errorState, onShowErrorMessage]);

  const RetrievingMessage = () => (
    <div className={styles.statusInformation}>
      <p>Retrieving classroom information</p>
      <Spinner name='line-scale' />
    </div>
  );

  const ImportingMessage = ({ completionPercent }: { completionPercent: number }) => (
    <div className={styles.statusInformation}>
      <p>Importing student list</p>
      <Spinner name='line-scale' fadeIn='none'></Spinner>
      <p>{`${completionPercent}%`}</p>
    </div>
  );

  const ClassroomsList = () => {
    if (!classrooms.list) {
      return null;
    }

    return (
      <>
        <ListGroup>
          {classrooms.list.map((classroom, i) => (
            <ListGroupItem key={i} className={styles.classroomItem}>
              <div className={styles.description}>
                <div className={styles.title}>{classroom.name}</div>
                {classroom.section && <div className={styles.details}>{classroom.section}</div>}
                <div className={styles.details}>{getStudentsList(classroom)}</div>
              </div>
              <div className={styles.actionIcons}>
                {/* Not yet implemented
              <IconWithTooltip tipText='Examine'>
               <i className='fa fa-eye'></i>
              </IconWithTooltip>
              */}
                <IconWithTooltip tipText='Sync'>
                  {/* note that createImportHandler() is being called for each classroom line item as it is rendered
                   * because we need a different one per line item
                   */}
                  <i className='fa fa-cloud-download' onClick={createImportHandler(i)}></i>
                </IconWithTooltip>
              </div>
            </ListGroupItem>
          ))}
        </ListGroup>
      </>
    );

    function getStudentsList(classroom: ExternalClassroom) {
      if (!classroom.students || classroom.students.length === 0) {
        return `No students`;
      }

      const list = classroom.students.reduce((acc, cur) => `${acc}${acc && ','} ${cur.profile.name.givenName}`, '');
      return list;
    }
  };

  const ModalHeader = () => (
    <>
      {deriveState() === 'syncListReady' && (
        <Modal.Header closeButton>
          <Modal.Title>
            <p className='ModalDialogHeader'>{props.title}</p>
          </Modal.Title>
        </Modal.Header>
      )}
    </>
  );

  /*
  const ModalBody = () => (
    <>
      {
        isDoneFetching && !isImporting ? <ClassroomsList /> :
          !isDoneFetching ? <RetrievingMessage /> : <ImportingMessage completionPercent={ importPercentage } />
      }
    </>
  );
  */
  const ModalBody = () => {
    switch (deriveState()) {
      case 'syncListReady':
        return <ClassroomsList />;

      case 'syncingClassroom':
        return <ImportingMessage completionPercent={importPercentage} />;

      case 'syncListNotReady':
      default:
        return <RetrievingMessage />;
    }
  };

  function deriveState() {
    // state variables
    //    isDoneFetching
    //    isImporting
    //    classrooms.source
    //    importSource
    if (!isDoneFetching) return 'syncListNotReady';
    // is done fetching, but...
    if (isImporting) return 'syncingClassroom';
    // is done fetching and is not importing anymore...
    if (importSource === classrooms.source) return 'syncListReady';
    return 'syncListNotReady';
  }

  if (props.show && errorState) {
    return null;
  }

  return (
    <>
      <Modal dialogClassName={cx(`ModalDialog`, styles.importClassroomModal)} show={props.show} onHide={props.onHide}>
        <ModalHeader />
        <Modal.Body>
          <ModalBody />
        </Modal.Body>
      </Modal>
    </>
  );

  function createImportHandler(classroomListIndex: number) {
    return async (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();

      if (!classrooms) {
        logger.logException(new Error(`ImportClassroomModal: classrooms is unexpectedly undefined`));
        return;
      }

      try {
        setIsImporting(true);
        setImportPercentage(0);
        const importParams = {
          idP: classrooms.source,
          defaultStudentPassword: getStore().getState().user.userProfile?.defaultStudentPassword || 'ilovepeekapak',
          rawClassroomData: { ...classrooms.list[classroomListIndex] },
          teachersUserId: getStore().getState().user.userProfile?.userId,
          operation: 'sync' as MergeOrSyncClassroomRequest['operation'],
        };
        const newClassroomObject = await importClassroomHelper(importParams);
        props.onDone(newClassroomObject);
      } catch (error) {
        logger.logException(error as Error);
        onShowErrorMessage(getErrorMessage(error as JobError), `Unable to proceed`, () => props.onDone(null));
      } finally {
        setIsImporting(false);
        props.onDone(null);
      }
    };

    function getErrorMessage(error: JobError) {
      let messageBuilder = `Please copy and paste the following error message and send to Peekapak support:<br /><strong>${error.message}`;

      if (error.time) {
        messageBuilder = `${messageBuilder} at ${error.time}`;
      }

      if (error.requestId) {
        messageBuilder = `${messageBuilder} on ${error.requestId}`;
      }

      if (error.relatedUid) {
        messageBuilder = `${messageBuilder} on ${error.relatedUid}`;
      }

      return messageBuilder;
    }
  }

  function importClassroomHelper(importParams: {
    idP: '' | ImportSource.CLEVER | ImportSource.GOOGLE;
    defaultStudentPassword: string;
    rawClassroomData: {
      id: string;
      name: string;
      section?: string | undefined;
      grade?: string | undefined;
      students: StudentContainer[];
    };
    teachersUserId: string | undefined;
    operation: MergeOrSyncClassroomRequest['operation'];
  }) {
    return new Promise<ClassroomType>((resolve, reject) => {
      importClassroom(
        importParams,
        async (newJob: { jobId: string }) => {
          try {
            let job: {
              status: string;
              data: string;
            } = await getJob(newJob.jobId);
            setImportPercentage(0);

            while (isJobPending(job)) {
              processPendingJob(job);
              await yieldAndWait();
              job = await getJob(newJob.jobId);
            }

            const data = JSON.parse(job.data);

            if (job.status === 'Done') {
              return resolve(data);
            }

            return reject(data);
          } catch (error) {
            return reject(error);
          }
        },
        (error) => {
          return reject(error);
        },
      );
    });

    function isJobPending(job: { status: string }) {
      return job.status !== 'Done' && job.status !== 'Error';
    }

    function processPendingJob(job: { data: string }) {
      if (job.data) {
        const data = JSON.parse(job.data);
        setImportPercentage(data.completionPercent);
      }
    }

    function yieldAndWait() {
      return new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 1000);
      });
    }
  }
};

export default ImportClassroomModal;
